import { useTranslation } from 'react-i18next';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle, Skeleton } from '@monksflow/monks-ui';
import type { PerformanceBreakdownRow } from '@/entities/media-campaign';
import { seriesVarForPlatform } from '../../lib/platform-palette';

export interface SpendShareProps {
  rows: PerformanceBreakdownRow[];
  isLoading?: boolean;
}

interface Share {
  row: PerformanceBreakdownRow;
  fraction: number;
}

/**
 * A share is already a ratio of rounded currency amounts, so a second decimal place
 * would imply precision the underlying spend figures do not have.
 */
function formatShare(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/**
 * Share of spend across platforms, as one horizontal stacked bar rather than a pie -
 * a pie makes small slices unreadable and this can hold up to eight platforms.
 */
export function SpendShare({ rows, isLoading = false }: SpendShareProps) {
  const { t } = useTranslation('projects');

  if (isLoading) {
    return <Skeleton className="h-16 w-full" data-testid="spend-share-skeleton" />;
  }

  // A row with spendReported === false never measured spend at all (e.g. an ad server).
  // Counting it in the denominator would draw it as a 0% slice, which reads as "bought
  // media and spent nothing" rather than "has nothing to say about spend".
  const reportingRows = rows.filter((row) => row.spendReported);

  if (reportingRows.length === 0) {
    return (
      <Empty data-testid="spend-share-empty">
        <EmptyHeader>
          <EmptyTitle>{t('performanceDashboard.spendShare.noPlatformReportsSpend')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  // A bar's geometry and every printed percentage are both a claim that each row was
  // divided out of the same total. We hold no FX rates, so that claim only holds when
  // every reporting row shares one currency - a null `currency` means a single row
  // already spans more than one (see PerformanceBreakdownRow's doc), which is just as
  // fabricated as combining two platforms billed in different currencies. We render no
  // bar rather than one bar per currency: this widget is a single glance at relative
  // share, and the mixed-currency Alert rendered above it already gives the per-currency
  // totals a second bar here couldn't add.
  const currencies = new Set(reportingRows.map((row) => row.currency));
  if (currencies.size > 1 || currencies.has(null)) {
    return (
      <Empty data-testid="spend-share-mixed-currency">
        <EmptyHeader>
          <EmptyTitle>{t('performanceDashboard.spendShare.spendCantBeCompared')}</EmptyTitle>
          <EmptyDescription>
            {reportingRows
              .map((row) =>
                t('performanceDashboard.spendShare.rowWithCurrency', {
                  label: row.label,
                  currency: row.currency ?? t('performanceDashboard.spendShare.mixedCurrencies'),
                }),
              )
              .join(', ')}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const total = reportingRows.reduce((sum, row) => sum + row.spend, 0);

  // Every reporting row measured $0, not nothing - a live campaign that has not spent
  // yet is a real, common state, distinct from "reports nothing on this scale" above.
  // Still guards the division below: with total 0, row.spend / total would be NaN.
  if (total <= 0) {
    return (
      <Empty data-testid="spend-share-zero">
        <EmptyHeader>
          <EmptyTitle>{t('performanceDashboard.spendShare.noSpendRecordedYet')}</EmptyTitle>
          {/* Naming the reporting platforms tells the marketer what is in scope and
              live-but-unspent, rather than leaving them wondering. */}
          <EmptyDescription>{reportingRows.map((row) => row.label).join(', ')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const shares: Share[] = reportingRows.map((row) => ({ row, fraction: row.spend / total }));

  return (
    <div className="flex flex-col gap-3" data-testid="spend-share">
      <div className="flex h-8 w-full gap-0.5 overflow-hidden rounded-sm bg-muted">
        {shares.map(({ row, fraction }) => (
          <div
            key={row.key}
            className="h-full"
            style={{ width: `${fraction * 100}%`, backgroundColor: seriesVarForPlatform(row.key) }}
            data-testid={`spend-share-segment-${row.key}`}
          />
        ))}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {shares.map(({ row, fraction }) => (
          <div key={row.key} className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: seriesVarForPlatform(row.key) }}
            />
            <span className="font-medium text-foreground">{row.label}</span>
            <span className="text-muted-foreground">{formatShare(fraction)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
